<!-- This component is the "view" component that renders the Signing Flow -->
<template>
	<div class="relative flex h-full w-full">
		<LeftSideBar
			:class="expanded === 'leftSideBar' ? 'w-1/4 2xl:w-1/5' : 'w-14'"
			:expanded="expanded === 'leftSideBar'"
			@expand="expanded = 'leftSideBar'"
		/>
		<div
			class="relative h-full min-w-0 grow"
			id="main-content"
		>
			<div
				id="step-content-container"
				class="h-[calc(100%-5rem)] transition-opacity duration-300"
				:class="
					sessionDocumentsStore.pageState.status === 'ready'
						? 'overflow-hidden'
						: 'overflow-y-auto'
				"
			>
				<FoxitPDFHandler
					v-show="
						sessionDocumentsStore.pageState.status === 'pending' ||
						sessionDocumentsStore.pageState.status === 'ready' ||
						sessionDocumentsStore.pageState.status === 'switch' ||
						sessionDocumentsStore.pageState.status === 'first-load'
					"
					ref="foxitPDFHandlerRef"
				/>
				<Transition
					name="fade"
					mode="out-in"
				>
					<KeepAlive v-if="setStepsComplete">
						<component :is="currentStepComponent" />
					</KeepAlive>
				</Transition>
			</div>
			<div id="edit-document-confirm-modal" />
			<StepFooter />
			<Loading v-if="isLoading" />
			<ConfirmIdentityModal
				v-if="sessionDocumentsStore.currentPlan"
				:plan="sessionDocumentsStore.currentPlan"
				:currentPlanIsEncrypted="sessionDocumentsStore.currentPlanIsEncrypted"
				@decryptUser="onDecryptUser"
			/>
			<AlternateFormToggle />
		</div>
		<RightSideBar
			:class="expanded === 'rightSideBar' ? 'w-1/4 min-w-xs' : 'w-14'"
			:expanded="expanded === 'rightSideBar'"
			@expand="expanded = 'rightSideBar'"
		/>
	</div>
</template>

<script setup lang="ts">
import CompleteStep from '@/components/documents/CompleteStep/CompleteStep.vue'
import ConfirmationStep from '@/components/documents/ConfirmationStep/ConfirmationStep.vue'
import EverplansStep from '@/components/documents/EverplansStep/EverplansStep.vue'
import AlternateFormToggle from '@/components/documents/Foxit/AlternateFormToggle.vue'
import FoxitPDFHandler from '@/components/documents/Foxit/FoxitPDFHandler.vue'
import LeftSideBar from '@/components/documents/LeftSideBar/LeftSideBar.vue'
import Loading from '@/components/documents/Loading.vue'
import OapStep from '@/components/documents/OapStep/OapStep.vue'
import PaymentStep from '@/components/documents/PaymentStep/PaymentStep.vue'
import RightSideBar from '@/components/documents/RightSideBar/RightSideBar.vue'
import StepFooter from '@/components/documents/StepFooter/StepFooter.vue'
import ConfirmIdentityModal from '@/components/encryption/ConfirmIdentityModal.vue'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { useDocumentStore } from '@/stores/document'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { computed, onBeforeMount, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const documentStore = useDocumentStore()
const planStore = usePlanStore()
const sessionStore = useSessionStore()
const sessionDocumentsStore = useSessionDocumentsStore()
const cloudSyncService = useCloudSyncService()
const expanded = ref<'leftSideBar' | 'rightSideBar'>('leftSideBar')
const appRoot = import.meta.env.VITE_APP_ROOT
const cacheName = `workbox-precache-v2-${appRoot}/`
const route = useRoute()
const setStepsComplete = ref<boolean>(false)

// This will load the Foxit PDF SDK stylesheet from the cache if it is available.
const loadFoxitStylesheet = async () => {
	const existingLink = document.getElementById('foxit-style')
	if (existingLink) {
		return
	}

	if (!navigator.onLine && import.meta.env.PROD) {
		try {
			const cache = await caches.open(cacheName)
			const cachedResponse = await cache.match(
				'/FoxitPDFSDKForWeb/lib/UIExtension.css'
			)

			if (cachedResponse) {
				const link = document.createElement('link')
				link.rel = 'stylesheet'
				link.href = cachedResponse.url
				link.id = 'foxit-style'

				document.head.appendChild(link)
				return
			}
		} catch (cacheError) {
			console.log('Cache not accessible:', cacheName, cacheError)
		}
	}

	const link = document.createElement('link')
	link.rel = 'stylesheet'
	link.href = '/FoxitPDFSDKForWeb/lib/UIExtension.css'
	link.id = 'foxit-style'

	link.onerror = () => {
		console.error('Failed to load UIExtension.css')
	}

	document.head.appendChild(link)
}

// This will remove the Foxit PDF SDK stylesheet from the DOM if it exists
const removeFoxitStylesheet = () => {
	const foxitStyle = document.getElementById('foxit-style')
	if (foxitStyle) {
		document.head.removeChild(foxitStyle)
	}
}

onBeforeMount(async () => {
	await cloudSyncService.flushPendingUpserts()
	await setupDocumentSignFlow()
	populateEverplansEmailWithPolicyEmail()
})

onUnmounted(() => {
	removeFoxitStylesheet()
})

// This function will populate the everplans email with the policy email for all active plans
const populateEverplansEmailWithPolicyEmail = async () => {
	for (const planHolder of sessionStore.activePlans) {
		try {
			await planStore.copyPolicyEmailToEverplansEmailForPlanHolder(planHolder)
		} catch (error) {
			console.error(
				`Error populating everplans email with policy email for plan holder: ${planHolder.id}`,
				error
			)
		}
	}
}

// This method will set the page state for the current plan after the user has decrypted
const onDecryptUser = async () => {
	await sessionDocumentsStore.setPageStateOnLoad(
		sessionDocumentsStore.currentPlan
	)
}

// This method will load the data needed for the document sign flow and set up the state for the flow.
const setupDocumentSignFlow = async () => {
	setStepsComplete.value = false

	await sessionDocumentsStore.setPageState('first-load')
	await documentStore.loadDocumentsForExistingPlanHolders()
	await documentStore.loadDocumentTemplates()

	const planIdFromQuery = route.query.planId as string | undefined
	const nextPlanHolderToSubmit = planIdFromQuery
		? sessionStore.getPlanHolder(planIdFromQuery)
		: sessionStore.getFirstPlanHolderNotConfirmed()

	const planId = getPlanHolderIDToOpenWith()
	if (planId) {
		sessionDocumentsStore.setCurrentPlanHolderID(planId)
	}

	await documentStore.initializeDocumentsForPlanHolders()
	await sessionDocumentsStore.setSteps()
	setStepsComplete.value = true

	if (!sessionDocumentsStore.currentPlanIsEncrypted) {
		await sessionDocumentsStore.setPageStateOnLoad(nextPlanHolderToSubmit)
	}

	await loadFoxitStylesheet()
}

// This method will return the default plan holder id to open the left sidebar with
const getPlanHolderIDToOpenWith = () => {
	if (route.query.planId) {
		const planFromRoute = sessionStore.getPlanHolder(
			route.query.planId as string
		)

		if (
			planFromRoute &&
			planFromRoute.packetStatus === PlanPacketStatus.Incomplete
		) {
			return route.query.planId as string
		}
	}

	return sessionStore.getFirstPlanHolderNotConfirmed()?.id
}

// Determines which component to load depending on page status
const currentStepComponent = computed(() => {
	switch (sessionDocumentsStore.pageState.status) {
		case 'confirmation':
			return ConfirmationStep
		case 'Payment Authorization':
			return PaymentStep
		case 'OAP Enrollment':
			return OapStep
		case 'Everplans Enrollment':
			return EverplansStep
		case 'complete':
			return CompleteStep
		default:
			return null
	}
})

const isLoading = computed(() => {
	return ['pending', 'merging', 'switch', 'first-load'].includes(
		sessionDocumentsStore.pageState.status
	)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
	transition:
		opacity 0.2s ease,
		transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
	transform: translateY(20px);
	opacity: 0;
	position: absolute;
	top: 0;
}
</style>
